import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import { jwtDecode } from "jwt-decode";
import { authAPI } from "../api/axiosClient";
import { LoginRequest } from "../api/types";

// JWT Token Types
interface JWTPayload {
  sub: string;
  jti: string;
  iat: number;
  UserName: string;
  UserFullName: string;
  UserType: string | string[]; // Array olarak geliyor: ["SchoolManager", "Parent"]
  "http://schemas.microsoft.com/ws/2008/06/identity/claims/role":
    | string
    | string[];
  Permission?: string[];
  nbf: number;
  exp: number;
  iss: string;
  aud: string;
}

// Types
interface User {
  id: string;
  username: string;
  fullName: string;
  email?: string;
  userType: string;
  role: string;
  permissions: string[];
}

// Role bazlı default permissions
const getDefaultPermissionsByRole = (role: string): string[] => {
  if (role === "SchoolManager") {
    return [
      "users.view",
      "users.create",
      "users.edit",
      "users.delete",
      "students.view",
      "students.create",
      "students.edit",
      "students.delete",
      "finance.view",
      "finance.create",
      "finance.edit",
      "events.view",
      "events.create",
      "events.edit",
      "events.delete",
      "schools.view",
      "classes.view",
      "parents.view",
      "reports.view",
    ];
  }

  if (role === "SchoolUser") {
    return ["students.view", "events.view"];
  }

  return [];
};


interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<boolean>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  setUser: (user: User | null) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
}

// Create Context
const AuthContext = createContext<AuthContextType | null>(null);

// Custom Hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Provider Component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // JWT Token decode fonksiyonu
  const decodeJWT = (token: string): JWTPayload | null => {
    try {
      return jwtDecode<JWTPayload>(token);
    } catch (error) {
      console.error("JWT decode error:", error);
      return null;
    }
  };

  // JWT'den kullanıcı bilgilerini çıkarma
const getUserFromToken = useCallback((token: string): User | null => {
  const decoded = decodeJWT(token);
  if (!decoded) return null;

  const userTypes = Array.isArray(decoded.UserType) ? decoded.UserType : [decoded.UserType];
  const role = userTypes.includes("SchoolManager") ? "SchoolManager" : userTypes[0];

  const tokenPermissions = decoded.Permission ?? [];
  const permissions =
    tokenPermissions.length > 0 ? tokenPermissions : getDefaultPermissionsByRole(role);

  return {
    id: decoded.sub,
    username: decoded.UserName,
    fullName: decoded.UserFullName,
    userType: userTypes.join(", "),
    role,
    permissions,
  };
}, []);


  // Token'ın süresi dolmuş mu kontrol et
  const isTokenExpired = (token: string): boolean => {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) return true;

    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
  };

  // Permission kontrol fonksiyonları
const hasPermission = useCallback(
  (permission: string) => {
    if (!user) return false;
    return user.permissions.includes(permission);
  },
  [user],
);

  const hasRole = useCallback(
    (role: string): boolean => {
      return user?.role === role;
    },
    [user?.role],
  );

  const hasAnyRole = useCallback(
    (roles: string[]): boolean => {
      if (!user?.role) return false;
      return roles.includes(user.role);
    },
    [user?.role],
  );

  // Check if user is already authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem("authToken");

        console.log("AuthContext checkAuth - token exists:", !!token);

        if (token) {
          // JWT token'ın süresi dolmuş mu kontrol et
          const expired = isTokenExpired(token);
          console.log("AuthContext checkAuth - token expired:", expired);

          if (!expired) {
            // Token geçerli, kullanıcı bilgilerini çıkar
            const userFromToken = getUserFromToken(token);
            console.log(
              "AuthContext checkAuth - userFromToken:",
              userFromToken,
            );
            if (userFromToken) {
              setUser(userFromToken);
              setIsAuthenticated(true);
              console.log("AuthContext checkAuth - user set successfully");
            } else {
              // Token geçersiz, temizle
              localStorage.removeItem("authToken");
              localStorage.removeItem("refreshToken");
              localStorage.removeItem("refreshTokenExpiresAt");
              localStorage.removeItem("userId");
            }
          } else {
            // Token süresi dolmuş, refresh token ile yenilemeyi dene
            try {
              const refreshSuccess = await refreshTokenFunction();
              if (!refreshSuccess) {
                // Refresh başarısız, temizle
                localStorage.removeItem("authToken");
                localStorage.removeItem("refreshToken");
                localStorage.removeItem("refreshTokenExpiresAt");
                localStorage.removeItem("userId");
              }
            } catch (error) {
              // Refresh hatası, temizle
              localStorage.removeItem("authToken");
              localStorage.removeItem("refreshToken");
              localStorage.removeItem("refreshTokenExpiresAt");
              localStorage.removeItem("userId");
            }
          }
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        // Clear any invalid tokens
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("refreshTokenExpiresAt");
        localStorage.removeItem("userId");
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login function
  const login = useCallback(
    async (credentials: LoginRequest): Promise<boolean> => {
      try {
        setIsLoading(true);
        const response = await authAPI.login(credentials);

        if (response.success && response.data) {
          const { token, refreshToken, refreshTokenExpiresAt, userId } =
            response.data;

          // Store tokens
          localStorage.setItem("authToken", token);
          localStorage.setItem("refreshToken", refreshToken);
          if (refreshTokenExpiresAt) {
            localStorage.setItem(
              "refreshTokenExpiresAt",
              refreshTokenExpiresAt,
            );
          }
          localStorage.setItem("userId", userId);

          // JWT'den kullanıcı bilgilerini çıkar
          const userFromToken = getUserFromToken(token);
          if (userFromToken) {
            setUser(userFromToken);
            setIsAuthenticated(true);
            return true;
          } else {
            console.error("Failed to decode user from token");
            return false;
          }
        } else {
          console.error("Login failed:", response.message);
          return false;
        }
      } catch (error) {
        console.error("Login error:", error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [getUserFromToken],
  );

  // Logout function
  const logout = useCallback(() => {
    // Clear local storage
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("refreshTokenExpiresAt");
    localStorage.removeItem("userId");
    localStorage.removeItem("rememberMe");

    // Clear state
    setUser(null);
    setIsAuthenticated(false);

    // Redirect to login (will be handled by router)
  }, []);

  // Refresh token function
  const refreshTokenFunction = useCallback(async (): Promise<boolean> => {
    try {
      const refreshTokenValue = localStorage.getItem("refreshToken");
      if (!refreshTokenValue) return false;

      const response = await authAPI.refreshToken({
        expiredAccessToken: localStorage.getItem("authToken"),
        refreshToken: refreshTokenValue,
      });

      if (response.success && response.data) {
        const {
          token,
          refreshToken: newRefreshToken,
          refreshTokenExpiresAt,
        } = response.data;

        // Update stored tokens
        localStorage.setItem("authToken", token);
        if (newRefreshToken) {
          localStorage.setItem("refreshToken", newRefreshToken);
        }
        if (refreshTokenExpiresAt) {
          localStorage.setItem("refreshTokenExpiresAt", refreshTokenExpiresAt);
        }

        // Yeni token'dan kullanıcı bilgilerini çıkar
        const userFromToken = getUserFromToken(token);
        if (userFromToken) {
          setUser(userFromToken);
          setIsAuthenticated(true);
        }

        return true;
      }
      return false;
    } catch (error) {
      console.error("Token refresh failed:", error);
      // If refresh fails, logout user
      logout();
      return false;
    }
  }, [logout, getUserFromToken]);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshToken: refreshTokenFunction,
    hasPermission,
    hasRole,
    hasAnyRole,
    setUser,
    setIsAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
